import React, { useState, useEffect } from 'react';
import { debugLog } from '../../utils/debug';
import { format } from 'date-fns';
import { 
  MapPinIcon, 
  PhoneIcon, 
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CloudIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { Mission, MissionType, Priority, Base } from '../../types';
import { formatMissionType, formatMissionStatus } from '../../utils/formatUtils';
import clsx from 'clsx';
import { parseCoordinateInput, formatCoordinates, validateWhat3Words, validateCoordinatesForBC } from '../../utils/coordinateUtils';
import LocationMap from '../LocationMap';
import weatherService from '../../services/weatherService';
import what3wordsService from '../../services/what3wordsService';
import geocodingService from '../../services/geocodingService';
import baseService from '../../services/baseService';

interface IncidentTabProps {
  mission: Mission;
  onUpdate: (updates: Partial<Mission>) => void;
  canEdit: boolean;
}

const IncidentTab: React.FC<IncidentTabProps> = ({ mission, onUpdate, canEdit }) => {
  const [localValues, setLocalValues] = useState<Partial<Mission>>({});
  const [coordinateInput, setCoordinateInput] = useState('');
  const [coordinateError, setCoordinateError] = useState<string | null>(null);
  const [coordinateWarning, setCoordinateWarning] = useState<string | null>(null);
  const [internationalMode, setInternationalMode] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [weather, setWeather] = useState<any>(null);
  const [metar, setMetar] = useState<any>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);
  const [bases, setBases] = useState<Base[]>([]);
  const [basesLoading, setBasesLoading] = useState(false);
  const [baseValidationError, setBaseValidationError] = useState('');
  const [mapLocation, setMapLocation] = useState<{
    lat: number | null;
    lon: number | null;
    title: string;
  }>({ lat: null, lon: null, title: '' });

  // Load bases on component mount
  useEffect(() => {
    const fetchBases = async () => {
      setBasesLoading(true);
      try {
        const activeBases = await baseService.getActiveBases();
        setBases(activeBases);
      } catch (error) {
        console.error('Error loading bases:', error);
      } finally {
        setBasesLoading(false);
      }
    };

    fetchBases();
  }, []);

  // Validate base selection
  useEffect(() => {
    if (!mission.primaryBaseId && canEdit) {
      setBaseValidationError('Primary base is required');
    } else {
      setBaseValidationError('');
    }
  }, [mission.primaryBaseId, canEdit]);

  const handleFieldChange = (field: keyof Mission, value: any) => {
    setLocalValues({ ...localValues, [field]: value });
    
    // Clear base validation error when base is selected
    if (field === 'primaryBaseId' && value) {
      setBaseValidationError('');
    }
  };

  const handleImmediateSave = async (field: keyof Mission, value: any) => {
    // For select fields, save immediately with the new value
    if (value !== mission[field]) {
      onUpdate({ [field]: value });
      setLocalValues({ ...localValues, [field]: value });
    }
  };

  const geocodeLocation = async (location: string) => {
    if (!location?.trim()) return;
    setLocationLoading(true);
    setGeocodeError(null);
    setCoordinateWarning(null);
    try {
      const results = await geocodingService.searchBC(location);

      if (results.length > 0) {
        const bestMatch = results[0];
        const lat = parseFloat(bestMatch.lat);
        const lon = parseFloat(bestMatch.lon);
        const foundName = geocodingService.formatResult(bestMatch);

        // Log for debugging (helps diagnose "wrong location" reports)
        debugLog(`[Geocode] "${location}" → ${foundName} (${lat}, ${lon})`);

        // Validate the found coordinates are in BC region
        const validation = validateCoordinatesForBC(lat, lon);
        if (validation.warning) {
          setCoordinateWarning(`Found: ${foundName}. ${validation.warning}`);
        } else {
          // Show what was found so dispatcher can verify
          setCoordinateWarning(`Found: ${foundName}`);
        }

        onUpdate({ sceneGpsLat: lat, sceneGpsLong: lon });
        setCoordinateInput(`${lat}, ${lon}`);
        setMapLocation({ lat, lon, title: foundName });
        setShowMap(true);
        fetchWeather(lat, lon);
      } else {
        setGeocodeError(`Could not find coordinates for "${location}". Try a simpler location name or enter coordinates directly.`);
      }
    } catch (err) {
      setGeocodeError('Geocoding service unavailable. Enter coordinates manually.');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleFieldBlur = async (field: keyof Mission) => {
    if (localValues[field] !== undefined && localValues[field] !== mission[field]) {
      const savedValue = localValues[field] as string;
      onUpdate({ [field]: localValues[field] });

      // Geocode location if pickup/scene location changed
      // Keep localValues populated until geocode completes so the field doesn't blank out
      if (field === 'pickupLocation' || field === 'sceneLocation') {
        await geocodeLocation(savedValue);
      }
      setLocalValues(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle coordinate input — accepts decimal, DMS, degrees-decimal-minutes, NMEA;
  // comma-separated, space-separated, or DMS with internal spaces
  const handleCoordinateBlur = () => {
    const input = coordinateInput.trim();
    if (!input) {
      setCoordinateError(null);
      setCoordinateWarning(null);
      return;
    }

    const pair = parseCoordinateInput(input);
    if (pair) {
      let lat = pair.latitude.decimal;
      let lon = pair.longitude.decimal;
      setCoordinateError(null);

      // In international mode, skip BC-specific auto-correction and validation
      if (internationalMode) {
        // Parse raw values without BC auto-correction (re-parse to get uncorrected lon)
        // The parser auto-corrects positive lon in BC range; for international we want raw
        const rawParts = input.split(/[,\s]+/).filter(s => s.length > 0);
        if (rawParts.length >= 2) {
          const rawLon = parseFloat(rawParts[1].replace(/[°'"NSEW]/gi, ''));
          if (!isNaN(rawLon) && rawLon > 0 && lon < 0 && Math.abs(rawLon) === Math.abs(lon)) {
            // Parser auto-negated; restore positive for international
            lon = rawLon;
          }
        }
        setCoordinateWarning('🌍 International mode: BC validation skipped');
      } else {
        // Validate coordinates are within expected BC region
        const validation = validateCoordinatesForBC(lat, lon);
        if (validation.warning) {
          setCoordinateWarning(validation.suggestion
            ? `${validation.warning}. ${validation.suggestion}`
            : validation.warning);
        } else {
          setCoordinateWarning(null);
        }
      }

      onUpdate({
        sceneGpsLat: lat,
        sceneGpsLong: lon
      });
      setCoordinateInput(`${lat}, ${lon}`);
      setMapLocation({
        lat,
        lon,
        title: `${lat.toFixed(6)}, ${lon.toFixed(6)}`
      });
      setShowMap(true);
      fetchWeather(lat, lon);
    } else {
      setCoordinateError('Could not parse coordinates. Try: 48.813826, -123.648911 or 48°48\'50"N 123°38\'56"W');
      setCoordinateWarning(null);
    }
  };

  // Handle What3Words input
  const handleWhat3WordsBlur = async () => {
    const w3w = localValues.what3words || mission.what3words;
    if (w3w && validateWhat3Words(w3w)) {
      setLocationLoading(true);
      try {
        // Try to convert what3words to coordinates
        const location = await what3wordsService.convertToCoordinates(w3w);
      if (location) {
        // Update coordinates from what3words
        const updates = {
          sceneGpsLat: location.coordinates.lat,
          sceneGpsLong: location.coordinates.lng,
          what3words: w3w
        };
        onUpdate(updates);
        
        // Update coordinate input and map
        setCoordinateInput(`${location.coordinates.lat}, ${location.coordinates.lng}`);
        setMapLocation({
          lat: location.coordinates.lat,
          lon: location.coordinates.lng,
          title: `${w3w} - ${location.nearestPlace}`
        });
        
        // Fetch weather for the new location
        fetchWeather(location.coordinates.lat, location.coordinates.lng);
      } else {
        // If API fails or no key, just update the map title
        setMapLocation(prev => ({
          ...prev,
          title: w3w
        }));
      }
      } finally {
        setLocationLoading(false);
      }
    }
  };

  // Fetch weather data
  const fetchWeather = async (lat: number, lon: number) => {
    setWeatherLoading(true);
    try {
      // Fetch both current weather and METAR in parallel
      const [currentWeather, metarData] = await Promise.all([
        weatherService.getCurrentWeather(lat, lon),
        weatherService.getMetarFromNOAA(lat, lon)
      ]);
      
      if (currentWeather) {
        setWeather(currentWeather);
      }
      
      if (metarData) {
        setMetar(metarData);
      }
    } catch (error) {
      console.error('Error fetching weather:', error);
    } finally {
      setWeatherLoading(false);
    }
  };

  // Initialize map and coordinates when mission loads
  useEffect(() => {
    if (mission.sceneGpsLat && mission.sceneGpsLong) {
      setCoordinateInput(`${mission.sceneGpsLat}, ${mission.sceneGpsLong}`);
      setMapLocation({
        lat: mission.sceneGpsLat,
        lon: mission.sceneGpsLong,
        title: mission.pickupLocation || mission.sceneLocation || 'Scene Location'
      });
      setShowMap(true);
      fetchWeather(mission.sceneGpsLat, mission.sceneGpsLong);
    } else if (mission.sceneLocation && !mission.sceneGpsLat) {
      // Auto-geocode if location text exists but no coordinates
      geocodeLocation(mission.sceneLocation);
    }
  }, [mission.sceneGpsLat, mission.sceneGpsLong, mission.pickupLocation, mission.sceneLocation]);

  const renderEditableField = (
    field: keyof Mission,
    label: string,
    type: 'text' | 'textarea' | 'select' = 'text',
    options?: { value: string; label: string }[]
  ) => {
    const rawValue = localValues[field] !== undefined ? localValues[field] : mission[field];
    const currentValue = (rawValue ?? '') as string;

    return (
      <div className="py-3">
        <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
        
        {canEdit ? (
          <div>
            {type === 'textarea' ? (
              <textarea
                value={currentValue || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                onBlur={() => handleFieldBlur(field)}
                className="form-input w-full"
                rows={3}
                placeholder={`Enter ${label.toLowerCase()}...`}
              />
            ) : type === 'select' ? (
              <select
                value={currentValue || ''}
                onChange={(e) => {
                  handleFieldChange(field, e.target.value);
                  handleImmediateSave(field, e.target.value); // Use immediate save for selects
                }}
                className="form-input w-full"
              >
                {options?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={currentValue || ''}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                onBlur={() => handleFieldBlur(field)}
                className="form-input w-full"
                placeholder={`Enter ${label.toLowerCase()}...`}
              />
            )}
          </div>
        ) : (
          <p className="text-gray-400">{String(mission[field] || '') || '--'}</p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Primary Base Selection - Required at Top */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <BuildingOffice2Icon className="h-5 w-5 mr-2" />
            Primary Base
            <span className="text-red-200 ml-1">*</span>
          </h3>
        </div>
        
        <div className="p-4">
          {canEdit ? (
            <div>
              <select
                value={localValues.primaryBaseId !== undefined ? localValues.primaryBaseId : (mission.primaryBaseId || '')}
                onChange={(e) => {
                  handleFieldChange('primaryBaseId', e.target.value);
                  handleImmediateSave('primaryBaseId', e.target.value);
                }}
                className={clsx(
                  'form-input w-full',
                  baseValidationError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                )}
                disabled={basesLoading}
              >
                <option value="">Select primary base...</option>
                {bases.map(base => (
                  <option key={base.id} value={base.id}>
                    {base.name} ({base.code})
                  </option>
                ))}
              </select>
              {baseValidationError && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  {baseValidationError}
                </p>
              )}
              {basesLoading && (
                <p className="mt-2 text-sm text-gray-500">Loading bases...</p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-gray-900 font-medium">
                {mission.primaryBase ? `${mission.primaryBase.name} (${mission.primaryBase.code})` : 'Not specified'}
              </p>
              {!mission.primaryBaseId && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  Primary base not set
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mission Overview */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title">Mission Overview</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="py-3">
              <label className="text-sm font-medium text-gray-700">Mission Number</label>
              <p className="text-gray-900 font-semibold">{mission.missionNumber}</p>
            </div>
            
            {renderEditableField(
              'missionType',
              'Mission Type',
              'select',
              Object.values(MissionType).map(type => ({
                value: type,
                label: formatMissionType(type)
              }))
            )}
            
            {renderEditableField(
              'priority',
              'Priority',
              'select',
              Object.values(Priority).map(priority => ({
                value: priority,
                label: priority
              }))
            )}
          </div>
          
          <div>
            <div className="py-3">
              <label className="text-sm font-medium text-gray-700">Time Received</label>
              <p className="text-gray-900">
                {mission.timeReceived 
                  ? format(new Date(mission.timeReceived), 'HH:mm dd/MM/yyyy')
                  : 'Not recorded'
                }
              </p>
            </div>
            
            <div className="py-3">
              <label className="text-sm font-medium text-gray-700">Current Status</label>
              <p className="text-gray-900">{formatMissionStatus(mission.status)}</p>
            </div>
            
            <div className="py-3">
              <label className="text-sm font-medium text-gray-700">Test/Training</label>
              {canEdit ? (
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mission.isTestTraining}
                    onChange={(e) => onUpdate({ isTestTraining: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">This is a test/training mission</span>
                </label>
              ) : (
                <p className="text-gray-900">{mission.isTestTraining ? 'Yes' : 'No'}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <MapPinIcon className="h-5 w-5 mr-2" />
            Location Information
            {locationLoading && (
              <span className="ml-2 text-sm text-green-200">
                (Looking up location...)
              </span>
            )}
          </h3>
        </div>
        
        <div className="space-y-4">
          {renderEditableField(
            'sceneLocation',
            'Scene Location',
            'textarea'
          )}
          
          {renderEditableField(
            'pickupLocation',
            'Pickup Location',
            'textarea'
          )}
          {canEdit && (
            <div className="mt-1 space-y-2">
              <p className="text-xs text-gray-500">
                Note: Location names are automatically geocoded to find coordinates. You can also enter coordinates directly below.
              </p>
              {!mission.sceneGpsLat && mission.sceneLocation && (
                <button
                  onClick={() => geocodeLocation(mission.sceneLocation!)}
                  disabled={locationLoading}
                  className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                >
                  {locationLoading ? 'Looking up...' : 'Lookup coordinates for scene location'}
                </button>
              )}
              {geocodeError && (
                <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
                  {geocodeError}
                </p>
              )}
            </div>
          )}
          
          {renderEditableField(
            'destinationLocation',
            'Destination Location',
            'textarea'
          )}
          
          <div className="py-3">
            <label className="text-sm font-medium text-gray-700 mb-1 block">What3Words</label>
            {canEdit ? (
              <input
                type="text"
                value={localValues.what3words !== undefined ? localValues.what3words : (mission.what3words || '')}
                onChange={(e) => handleFieldChange('what3words', e.target.value)}
                onBlur={() => {
                  handleFieldBlur('what3words');
                  handleWhat3WordsBlur();
                }}
                className="form-input w-full"
                placeholder="word.word.word"
              />
            ) : (
              <p className="text-gray-900">{mission.what3words || 'Not specified'}</p>
            )}
          </div>
          
          <div className="py-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700">Scene Coordinates (Lat, Long)</label>
              {canEdit && (
                <label className="flex items-center text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={internationalMode}
                    onChange={(e) => setInternationalMode(e.target.checked)}
                    className="mr-1 h-3 w-3"
                  />
                  🌍 International (repatriation)
                </label>
              )}
            </div>
            {canEdit ? (
              <>
                <input
                  type="text"
                  value={coordinateInput}
                  onChange={(e) => {
                    setCoordinateInput(e.target.value);
                    setCoordinateError(null);
                  }}
                  onBlur={handleCoordinateBlur}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  className={clsx(
                    'form-input w-full',
                    coordinateError && 'border-red-300 focus:border-red-500',
                    coordinateWarning && !coordinateError && 'border-yellow-400 focus:border-yellow-500'
                  )}
                  placeholder="48.813826, -123.648911 or 48°48'50&quot;N 123°38'56&quot;W"
                />
                {coordinateError && (
                  <p className="mt-1 text-sm text-red-600">{coordinateError}</p>
                )}
                {coordinateWarning && !coordinateError && (
                  <p className="mt-1 text-sm text-yellow-700 bg-yellow-50 px-2 py-1 rounded">
                    ⚠️ {coordinateWarning}
                  </p>
                )}
              </>
            ) : (
              <p className="text-gray-900">
                {mission.sceneGpsLat && mission.sceneGpsLong
                  ? `${mission.sceneGpsLat}, ${mission.sceneGpsLong}`
                  : 'Not specified'}
              </p>
            )}
          </div>
          
          {mission.sceneGpsLat && mission.sceneGpsLong && (
            <div className="mt-2 text-sm text-gray-600">
              Aviation Format: {formatCoordinates(mission.sceneGpsLat, mission.sceneGpsLong, 'aviation')}
            </div>
          )}
        </div>
      </div>

      {/* Caller Information */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <PhoneIcon className="h-5 w-5 mr-2" />
            Caller Information
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            {renderEditableField(
              'callerAgency',
              'Agency'
            )}
            
            {renderEditableField(
              'callerName',
              'Caller Name'
            )}
          </div>
          
          <div>
            {renderEditableField(
              'callerPhone',
              'Phone Number'
            )}
            
            {renderEditableField(
              'callerIncidentNumber',
              'Incident Number'
            )}
          </div>
        </div>
      </div>

      {/* Incident Notes */}
      <div className="card">
        <div className="card-header card-header-brand">
          <h3 className="card-title flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            Incident Notes
          </h3>
        </div>
        
        {renderEditableField(
          'incidentNotes',
          'Notes',
          'textarea'
        )}
      </div>

      {/* Map and Weather */}
      {showMap && mapLocation.lat && mapLocation.lon && (
        <div className="card">
          <div className="card-header card-header-brand">
            <h3 className="card-title flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2" />
              Scene Location Map
            </h3>
          </div>
          
          <LocationMap
            latitude={mapLocation.lat}
            longitude={mapLocation.lon}
            title={mapLocation.title}
            height="400px"
          />
          
          {weatherLoading && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-blue-700">Loading weather data...</p>
            </div>
          )}
          
          {weather && !weatherLoading && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                <CloudIcon className="h-5 w-5 mr-2" />
                Current Weather at Scene ({mission.sceneGpsLat?.toFixed(4)}, {mission.sceneGpsLong?.toFixed(4)})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Conditions:</span>
                  <p className="font-medium text-blue-900 capitalize">{weather.description}</p>
                </div>
                <div>
                  <span className="text-blue-700">Temperature:</span>
                  <p className="font-medium text-blue-900">{weather.temperature}°C (feels {weather.feelsLike}°C)</p>
                </div>
                <div>
                  <span className="text-blue-700">Wind:</span>
                  <p className="font-medium text-blue-900">{weather.windDirection}° @ {weather.windSpeed} km/h</p>
                </div>
                <div>
                  <span className="text-blue-700">Visibility:</span>
                  <p className="font-medium text-blue-900">{weather.visibility.toFixed(1)} km</p>
                </div>
                <div>
                  <span className="text-blue-700">Humidity:</span>
                  <p className="font-medium text-blue-900">{weather.humidity}%</p>
                </div>
                <div>
                  <span className="text-blue-700">Pressure:</span>
                  <p className="font-medium text-blue-900">{weather.pressure} hPa</p>
                </div>
                <div>
                  <span className="text-blue-700">Cloud Cover:</span>
                  <p className="font-medium text-blue-900">{weather.clouds}%</p>
                </div>
              </div>
            </div>
          )}
          
          {metar && !weatherLoading && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
                ✈️ Nearest Airport METAR - {metar.station} ({metar.distance}km away)
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-yellow-700">Flight Rules:</span>
                  <span className={`font-bold ${
                    metar.flightRules === 'VFR' ? 'text-green-600' :
                    metar.flightRules === 'MVFR' ? 'text-blue-600' :
                    metar.flightRules === 'IFR' ? 'text-red-600' : 'text-purple-600'
                  }`}>{metar.flightRules}</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-yellow-700">Temp/Dewpoint:</span>
                    <p className="font-medium text-yellow-900">{metar.temperature}°C / {metar.dewpoint}°C</p>
                  </div>
                  <div>
                    <span className="text-yellow-700">Wind:</span>
                    <p className="font-medium text-yellow-900">
                      {metar.windDirection}° @ {Math.round(metar.windSpeed)} km/h
                      {metar.windGust ? ` G${Math.round(metar.windGust)}` : ''}
                    </p>
                  </div>
                  <div>
                    <span className="text-yellow-700">Visibility:</span>
                    <p className="font-medium text-yellow-900">{metar.visibility.toFixed(1)} km</p>
                  </div>
                  <div>
                    <span className="text-yellow-700">Altimeter:</span>
                    <p className="font-medium text-yellow-900">{metar.altimeter.toFixed(2)} inHg</p>
                  </div>
                </div>
                {metar.clouds.length > 0 && (
                  <div>
                    <span className="text-yellow-700">Clouds:</span>
                    <p className="font-medium text-yellow-900">
                      {metar.clouds.map((cloud: any, idx: number) => 
                        `${cloud.type} at ${cloud.altitude}ft${idx < metar.clouds.length - 1 ? ', ' : ''}`
                      )}
                    </p>
                  </div>
                )}
                <div className="mt-2 p-2 bg-yellow-100 rounded font-mono text-xs">
                  {metar.rawText}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <div className="text-sm text-gray-500 italic">
          Tip: All fields auto-save when you click away or press Tab. Enter coordinates in any format.
        </div>
      )}
    </div>
  );
};

export default IncidentTab;